import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { getAllProgress } from '../db';
import './StudyCalendar.css';

const OP_LABEL = { study: '学', test: '测', review1: 'R1', review2: 'R2', review3: 'R3' };
const OP_COLOR = { study: '#ea580c', test: '#f97316', review1: '#5b7c99', review2: '#6b8e7a', review3: '#8a9ba8' };
const WEEKDAYS = ['一', '二', '三', '四', '五', '六', '日'];
const OP_ORDER = ['study', 'test', 'review1', 'review2', 'review3'];

function pad(n) { return String(n).padStart(2, '0'); }

function StudyCalendar() {
    const navigate = useNavigate();
    const [dayMap, setDayMap] = useState({});

    useEffect(() => {
        api.getCalendar()
            .then(res => {
                const m = {};
                res.data.forEach(item => {
                    m[item.date] = { activities: item.activities, durationMin: item.duration_min || 0 };
                });
                setDayMap(m);
            })
            .catch(async (err) => {
                // 离线降级: 从 IndexedDB progress 本地按日聚合 (复用后端 calendar 逻辑)
                console.error("获取日历失败, 降级读离线缓存:", err);
                const allProgress = await getAllProgress();
                const tmp = {};
                const dayDurSec = {};   // day -> 累计学习秒数(离线按本地 duration_sec 求和, 与后端一致)
                allProgress.forEach(p => {
                    const day = (p.completed_at || '').slice(0, 10);
                    if (!day) return;
                    if (!tmp[day]) tmp[day] = {};
                    if (!tmp[day][p.op_type]) tmp[day][p.op_type] = { groups: [], orig: new Set() };
                    const ng = String(p.new_group_id != null ? p.new_group_id : p.group_id);
                    const gid = ng.split('.')[0];
                    if (!tmp[day][p.op_type].groups.includes(ng)) tmp[day][p.op_type].groups.push(ng);
                    if (!tmp[day][p.op_type].orig.has(gid)) tmp[day][p.op_type].orig.add(gid);
                    if (!dayDurSec[day]) dayDurSec[day] = 0;
                    dayDurSec[day] += (p.duration_sec || 0);
                });
                const m = {};
                Object.keys(tmp).forEach(day => {
                    const activities = OP_ORDER.filter(op => tmp[day][op]).map(op => ({ op_type: op, groups: tmp[day][op].groups, orig_groups: Array.from(tmp[day][op].orig) }));
                    const totalSec = dayDurSec[day] || 0;
                    const durationMin = totalSec > 0 ? Math.round(totalSec / 60) : 0;
                    m[day] = { activities, durationMin };
                });
                setDayMap(m);
            });
    }, []);

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const currentMonth = `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;

    // 月份渲染顺序: 当前月 + 有记录月份(去除当前月, 倒序)
    const recordMonths = Object.keys(dayMap).map(d => d.slice(0, 7));
    const uniqueRecordMonths = Array.from(new Set(recordMonths)).sort().reverse();
    const renderMonths = [currentMonth, ...uniqueRecordMonths.filter(m => m !== currentMonth)];

    function renderMonth(monthStr) {
        const [yStr, mStr] = monthStr.split('-');
        const y = parseInt(yStr), m = parseInt(mStr);
        const firstDay = new Date(y, m - 1, 1).getDay();      // 0=Sun
        const offset = (firstDay + 6) % 7;                      // 周一起始的空格数
        const daysInMonth = new Date(y, m, 0).getDate();
        // 统计当月学习情况(split): X=不同原 group 数, Y=不同拆分组(new_group_id)数
        // 仅按 op_type='study' 计"完成/学习了几个组"
        const origGroups = new Set();   // 原 group_id
        const splitGroups = new Set();  // new_group_id
        Object.keys(dayMap).forEach(dateStr => {
            if (dateStr.slice(0, 7) === monthStr) {
                const info = dayMap[dateStr];
                if (info && info.activities) info.activities.forEach(a => {
                    if (a.op_type === 'study') {
                        (a.orig_groups || []).forEach(g => origGroups.add(g));
                        (a.groups || []).forEach(g => splitGroups.add(g));
                    }
                });
            }
        });
        const origCount = origGroups.size;
        const splitCount = splitGroups.size;
        const cells = [];
        for (let i = 0; i < offset; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        while (cells.length % 7 !== 0) cells.push(null);

        return (
            <div className="cal-month" key={monthStr}>
                <div className="cal-month-title">
                    {y}年 {m}月 · <span className="cal-month-count">（学习了{origCount}个group，包含{splitCount}个拆分组）</span>
                </div>
                <div className="cal-grid">
                    {WEEKDAYS.map(w => <div className="cal-weekday" key={w}>{w}</div>)}
                    {cells.map((d, i) => {
                        if (d === null) return <div className="cal-day empty" key={i} />;
                        const dateStr = `${monthStr}-${pad(d)}`;
                        const dayInfo = dayMap[dateStr];
                        const acts = dayInfo ? dayInfo.activities : undefined;
                        const durationMin = dayInfo ? dayInfo.durationMin : 0;
                        const isActive = !!acts;
                        const isToday = dateStr === todayStr;
                        const cls = ['cal-day'];
                        if (isActive) cls.push('active');
                        if (isToday) cls.push('today');
                        return (
                            <div className={cls.join(' ')} key={i}>
                                <div className="cal-day-head">
                                    {durationMin > 0 && (
                                        <span className="cal-duration">{durationMin}分钟</span>
                                    )}
                                    <div className="cal-day-num">{d}</div>
                                </div>
                                {acts && acts.map((a, j) => (
                                    <div className="cal-act" key={j} style={{ color: OP_COLOR[a.op_type] || '#475569' }}>
                                        {OP_LABEL[a.op_type] || a.op_type} G{a.groups.join(',')}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="cal-page">
            <div className="cal-container">
                <h1 className="cal-title">📅 学习日历</h1>
                {renderMonths.map(renderMonth)}
            </div>
            <button className="back-home" onClick={() => navigate('/')}>返回首页</button>
        </div>
    );
}

export default StudyCalendar;
